import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Newspaper, BookOpenCheck, Sparkles, ArrowRight } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { useNavigate } from 'react-router-dom';

const LS_KEY = 'c2r-newformat-modal-seen-v1';

/**
 * Modale one-shot d'annonce aux utilisateurs CONNECTÉS qui suivaient l'ancien
 * format QCM. Affichée une seule fois, mémorisation localStorage.
 *
 * Cibles : élèves connectés ayant un compte antérieur au pivot éditorial.
 * Les invités anonymes voient la NewFormatBanner uniquement.
 */
export function NewFormatWelcomeModal() {
  const { user } = useApp();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // N'afficher qu'aux utilisateurs connectés NON admin/enseignant
    if (!user) return;
    if (user.role === 'admin' || user.role === 'enseignant') return;
    try {
      const seen = localStorage.getItem(LS_KEY);
      if (seen !== '1') {
        // Léger délai pour ne pas court-circuiter le rendu initial
        const t = setTimeout(() => setOpen(true), 800);
        return () => clearTimeout(t);
      }
    } catch (e) { /* ignore */ }
  }, [user]);

  const handleClose = () => {
    try { localStorage.setItem(LS_KEY, '1'); } catch (e) { /* ignore */ }
    setOpen(false);
  };

  const handleDiscover = () => {
    handleClose();
    navigate('/nouveau-format');
  };

  if (!user || user.role === 'admin' || user.role === 'enseignant') return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-lg" data-testid="newformat-welcome-modal">
        <DialogHeader>
          <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center mb-3 mx-auto">
            <Newspaper className="w-6 h-6" />
          </div>
          <DialogTitle className="text-center text-xl">Code 2 Roues évolue ✨</DialogTitle>
          <DialogDescription className="text-center">
            On change de cap pour vous offrir une expérience d'apprentissage <strong>plus moderne</strong>, plus proche d'un magazine que d'un QCM à rallonge.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 my-2">
          <div className="flex gap-3 p-3 rounded-lg bg-muted/40 border border-border">
            <BookOpenCheck className="w-5 h-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <strong className="block">Articles éditoriaux par thématique ETM</strong>
              <span className="text-xs text-muted-foreground">Circulation, conducteur, route, mécanique… chaque sujet traité en profondeur, avec photos et encadrés pédagogiques.</span>
            </div>
          </div>
          <div className="flex gap-3 p-3 rounded-lg bg-muted/40 border border-border">
            <Sparkles className="w-5 h-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <strong className="block">Fini les sessions chronométrées</strong>
              <span className="text-xs text-muted-foreground">Plus de pression du score. On apprend à son rythme, comme on lit un magazine. La compréhension prime sur la mémorisation.</span>
            </div>
          </div>
        </div>

        <p className="text-xs text-muted-foreground text-center px-2">
          Votre compte et votre historique sont conservés. Le nouveau format est déjà accessible — il suffit d'explorer les rubriques.
        </p>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleClose} data-testid="newformat-modal-later">Plus tard</Button>
          <Button onClick={handleDiscover} data-testid="newformat-modal-discover">
            Découvrir le nouveau format <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
